import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserSessionContext } from 'features/session/userSessionStore';
import { Modal } from 'shared/components/ui/modal';
import { loginWithPlatform } from './platformLogin';
import { bootstrapFromPlatformSessionId } from './bootstrapFromSessionId';

/** Extract a user-facing message from login/bootstrap errors (including Axios 400 response body). */
function getLoginErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    const ax = err as { response?: { data?: unknown; status?: number } };
    if (ax.response?.data && typeof ax.response.data === 'object') {
      const data = ax.response.data as { error?: string; message?: string; details?: unknown };
      if (typeof data.error === 'string' && data.error) return data.error;
      if (typeof data.message === 'string' && data.message) return data.message;
    }
    return err.message;
  }
  return String(err);
}

export type LoginModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export function LoginModal({ open, onClose, onSuccess }: LoginModalProps) {
  const { t } = useTranslation();
  const [, dispatch] = useUserSessionContext();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError(t('login.errorRequired', 'Email and password are required'));
      return;
    }
    setLoading(true);
    try {
      const res = await loginWithPlatform({
        email_address: email.trim(),
        password,
        remember_me: rememberMe,
      });
      await bootstrapFromPlatformSessionId(res.session_id, dispatch);
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('s', res.session_id);
        window.history.replaceState({}, '', url.toString());
      } catch {
        // ignore URL update
      }
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message = getLoginErrorMessage(err);
      setError(message || t('login.errorFailed', 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('LOGIN', 'Login')}
      description={t('login.description', 'Sign in to access your wallet and place bets')}
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-md bg-destructive/10 text-destructive text-sm p-3" role="alert">
            {error}
          </div>
        )}
        <div>
          <label htmlFor="login-email" className="block text-sm font-medium text-foreground mb-1">
            {t('login.email', 'Email')}
          </label>
          <input
            id="login-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            placeholder="you@example.com"
            disabled={loading}
          />
        </div>
        <div>
          <label htmlFor="login-password" className="block text-sm font-medium text-foreground mb-1">
            {t('login.password', 'Password')}
          </label>
          <input
            id="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            disabled={loading}
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            id="login-remember"
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="rounded border-input"
            disabled={loading}
          />
          <label htmlFor="login-remember" className="text-sm text-foreground">
            {t('login.rememberMe', 'Remember me')}
          </label>
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-border bg-background text-foreground text-sm font-medium hover:bg-muted"
            disabled={loading}
          >
            {t('CANCEL', 'Cancel')}
          </button>
          <button
            type="submit"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 disabled:opacity-50"
            disabled={loading}
          >
            {loading ? t('login.signingIn', 'Signing in...') : t('login.signIn', 'Sign in')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
